import { css, html, nothing, type PropertyValues } from "lit";
import { ActiveAutoSpinElement, defineActiveAutoSpinElement } from "../../autospin/ActiveAutoSpinElement";
import { AUTOSPIN_POPUP_LABELS, AutoSpinPopupElement, defineAutoSpinPopupElement } from "../../autospin/AutoSpinPopupElement";
import type { BalancePort } from "../../balance/BalancePort";
import type { BetPort } from "../../bet/BetPort";
import type { CanvasOverlayStore } from "../../canvas-overlay/CanvasOverlayStore";
import { ChromeElement, defineChromeElement, pressFeedback, pressHaptic } from "../../chrome/ChromeElement";
import { artImage, artMask, type ChromeArt } from "../../chrome/chromeArt";
import { fallbackLabels, type LabelTable, type Labels } from "../../chrome/chromeLabels";
import { chromeControlStyles } from "../../chrome/chromeStyles";
import { fitTexts } from "../../chrome/fitText";
import { FEATURE_HUD_LABELS, FeatureHudElement, defineFeatureHudElement } from "../../feature-hud/FeatureHudElement";
import type { FeatureModeBadgePort, FeatureModeDisableButtonBindings, FeatureModeDisableButtonPort } from "../../feature-mode/FeatureModePorts";
import type { SpinSpeedMode } from "../../spin/SpinCoordinatorTypes";
import type { CurrentWinPort } from "../../win/current-win/CurrentWinPort";
import { BetChangePopupElement, defineBetChangePopupElement } from "../bet-popup/BetChangePopupElement";
import { SHELL_MENU_LABELS, ShellMenuElement, defineShellMenuElement } from "../menu/ShellMenuElement";
import type { ShellMenuTogglePort } from "../menu/ShellMenuPorts";
import { SPEED_MODE_POPUP_LABELS, SpeedModePopupElement, defineSpeedModePopupElement } from "../speed-mode/SpeedModePopupElement";
import type { SpeedModeTogglePort } from "../speed-mode/SpeedModePorts";
import { SpinButtonElement, defineSpinButtonElement } from "../spin/SpinButtonElement";
import type { SpinButtonPort } from "../spin/SpinButtonPort";
import { BarButton } from "./BarButton";
import type { ControlBarPort } from "./ControlBarPort";
import { MoneyLabelElement, defineMoneyLabelElement } from "./MoneyLabelElement";
import { BET_BUTTON_SIZE, MOBILE_BAR_PLACES as P, PURCHASE_SIZE, SPIN_SIZE, TOGGLE_SIZE, portraitBarDrops, type ControlBarLayout } from "./portraitBarLayout";
import type { ScreenShape } from "@slot/presentation";

export const CONTROL_BAR_LABELS = {
  balance: ["common.balance", "BALANCE"],
  bet: ["common.bet", "Bet"],
  win: ["common.win", "Win"],
  holdToSpin: ["input.hold_to_spin", "Hold to Spin"],
  buyBonus: ["feature_purchase.buy_bonus", "Buy\nBonus"],
  disable: ["feature_mode.disable", "Disable"],
  gamble: ["gamble.title", "Gamble"],
  autospinsLeft: ["autospin.autospins_left", "Autospins left"],
  hud: FEATURE_HUD_LABELS,
  menu: SHELL_MENU_LABELS,
  autospin: AUTOSPIN_POPUP_LABELS,
  speed: SPEED_MODE_POPUP_LABELS,
} as const satisfies LabelTable;
export type ControlBarLabels = Labels<typeof CONTROL_BAR_LABELS>;

export const CONTROL_BAR_TAG = "slot-control-bar";

/**
 * `<slot-control-bar>`: the game's controls laid out per shape — the desktop bar, the portrait
 * groups, the landscape right column — and the popups it anchors. Every control exists once; the
 * template puts it in the shape's group and the stylesheet places it there. The child elements and
 * the small adapters are the ports the presenters drive (`ControlBarPort`). Empty bar area takes no
 * pointer events: the grid's slam surface underneath keeps working.
 */
export class ControlBarElement extends ChromeElement implements ControlBarPort {
  public static override properties = {
    ...ChromeElement.properties,
    art: { attribute: false },
    labels: { attribute: false },
    layout: { attribute: false },
    holdToSpin: { attribute: false },
    bet: { state: true },
    badge: { state: true },
    speedMode: { state: true },
    menuOpen: { state: true },
    replayMode: { state: true },
    holdHint: { state: true },
  };

  public declare art: ChromeArt;
  public declare labels: ControlBarLabels;
  /** The title's portrait arrangements (decision 78); none, the shell's places. */
  public declare layout: ControlBarLayout;
  /** The operator's hold-to-spin setting: the mobile "Hold to Spin" caption shows only with it on, never in replay mode, and only while the spin input's idle hint asks for it (decision 36). */
  public declare holdToSpin: boolean;
  private canvasOverlayStore: CanvasOverlayStore | null = null;
  private unsubscribeCanvasOverlay: (() => void) | null = null;

  private readonly spinButtonElement = new SpinButtonElement();
  public readonly activeAutoSpin = new ActiveAutoSpinElement();
  public readonly featureHud = new FeatureHudElement();
  public readonly betChangePopup = new BetChangePopupElement();
  public readonly menu = new ShellMenuElement();
  public readonly autoSpinPopup = new AutoSpinPopupElement();
  public readonly speedPopup = new SpeedModePopupElement();
  private readonly balanceLabel = new MoneyLabelElement();
  private readonly betLabel = new MoneyLabelElement();
  private readonly winLabel = new MoneyLabelElement();

  public readonly autoSpinToggle = new BarButton(this, true);
  public readonly purchaseToggle = new BarButton(this, false);
  public readonly gambleEntry = new BarButton(this, false);
  private readonly disableButton = new BarButton(this, false);
  private readonly speedButton = new BarButton(this, true);
  private readonly menuButton = new BarButton(this, true);
  private readonly betPlus = new BarButton(this, true);
  private readonly betMinus = new BarButton(this, true);
  private declare speedMode: SpinSpeedMode;
  private declare menuOpen: boolean;
  /** Replay mode, for the page's life: the hold caption goes with the spin button it describes. */
  private declare replayMode: boolean;
  /** The spin input's idle hold hint (decision 36): the mobile caption shows only while it is asked for. */
  private declare holdHint: boolean;
  private declare bet: { text: string; progress: number; valueVisible: boolean; controlsVisible: boolean; featureMode: boolean };
  private declare badge: { visible: boolean; fading: boolean; fadeSeconds: number; title: string; generation: number };
  private disableBindings: FeatureModeDisableButtonBindings | null = null;

  public constructor() {
    super();
    this.art = {};
    this.labels = fallbackLabels(CONTROL_BAR_LABELS);
    this.layout = {};
    this.holdToSpin = true;
    this.speedMode = "normal";
    this.menuOpen = false;
    this.replayMode = false;
    this.holdHint = false;
    this.bet = { text: "", progress: 1, valueVisible: true, controlsVisible: true, featureMode: false };
    this.badge = { visible: false, fading: false, fadeSeconds: 0, title: "", generation: 0 };
    this.winLabel.shown = false;
    this.balanceLabel.dataset.part = "balance-label";
    this.betLabel.dataset.part = "bet-label";
    this.winLabel.dataset.part = "win-label";
  }

  // ── The composite ports (state the template renders) ────────────────────────────────────

  /**
   * The spin control: the face is the child element; its idle hold hint is the bar's mobile caption,
   * which sits in the bar's layout, not on the button (decision 36).
   */
  public readonly spinButton: SpinButtonPort = {
    bind: (bindings) => this.spinButtonElement.bind(bindings),
    setLocked: (locked) => this.spinButtonElement.setLocked(locked),
    setVisible: (visible) => this.spinButtonElement.setVisible(visible),
    setSlamVisible: (visible) => this.spinButtonElement.setSlamVisible(visible),
    setEnabled: (enabled) => this.spinButtonElement.setEnabled(enabled),
    setFeatureModeStyle: (active) => this.spinButtonElement.setFeatureModeStyle(active),
    setHoldHintVisible: (visible) => (this.holdHint = visible),
    playHoldWarmupVisual: () => this.spinButtonElement.playHoldWarmupVisual(),
    stopHoldWarmupVisual: () => this.spinButtonElement.stopHoldWarmupVisual(),
    resetHoldWarmupVisualImmediate: () => this.spinButtonElement.resetHoldWarmupVisualImmediate(),
  };

  public readonly balance: BalancePort = {
    setBalanceText: (text) => (this.balanceLabel.amount = text),
    hide: () => (this.balanceLabel.shown = false),
  };

  public readonly currentWin: CurrentWinPort = {
    setVisible: (visible) => (this.winLabel.shown = visible),
    setText: (text) => (this.winLabel.amount = text),
  };

  public readonly betPanel: BetPort = {
    bind: ({ onMinus, onPlus }) => {
      this.betMinus.setOnTap(onMinus);
      this.betPlus.setOnTap(onPlus);
    },
    setBetText: (text) => this.updateBet({ text }),
    setBetProgress: (progress) => this.updateBet({ progress: Math.max(0, Math.min(1, progress)) }),
    setLocked: (locked) => {
      this.betMinus.setInteractable(!locked);
      this.betPlus.setInteractable(!locked);
    },
    setMinusEnabled: (enabled) => this.betMinus.setInteractable(enabled),
    setPlusEnabled: (enabled) => this.betPlus.setInteractable(enabled),
    setBetValueVisible: (visible) => this.updateBet({ valueVisible: visible }),
    setControlsVisible: (visible) => this.updateBet({ controlsVisible: visible }),
    setFeatureModeActive: (active) => this.updateBet({ featureMode: active }),
  };

  public readonly featureModeBadge: FeatureModeBadgePort = {
    setVisible: (visible) => (this.badge = { ...this.badge, visible, fading: false, generation: this.badge.generation + 1 }),
    // The fade runs for the time asked; until 2026-09-26 the CSS fixed it at 0.5 s whatever was passed (review finding 68).
    fadeOut: (durationSeconds) => {
      if (this.badge.visible) this.badge = { ...this.badge, fading: true, fadeSeconds: durationSeconds };
    },
    setTitle: (title) => (this.badge = { ...this.badge, title }),
  };

  public readonly featureModeDisable: FeatureModeDisableButtonPort = {
    bind: (bindings) => (this.disableBindings = bindings),
    setVisible: (visible) => this.disableButton.setShown(visible),
    setInteractable: (interactable) => this.disableButton.setInteractable(interactable),
  };

  public readonly speedToggle: SpeedModeTogglePort = {
    setShown: (shown) => this.speedButton.setShown(shown),
    setOnTap: (onTap) => this.speedButton.setOnTap(onTap),
    setMode: (mode) => (this.speedMode = mode),
  };

  public readonly menuToggle: ShellMenuTogglePort = {
    setShown: (shown) => this.menuButton.setShown(shown),
    setOnTap: (onTap) => this.menuButton.setOnTap(onTap),
    setOpen: (open) => (this.menuOpen = open),
  };

  public enterReplayMode(): void {
    this.spinButton.setVisible(false);
    this.replayMode = true;
    this.betChangePopup.hideImmediate(false);
    this.menu.setMenuOpen(false);
    this.menuButton.setShown(false);
    this.featureModeBadge.setVisible(false);
    this.purchaseToggle.setShown(false);
    this.disableButton.setShown(false);
    this.autoSpinToggle.setShown(false);
    this.autoSpinPopup.hide();
    this.speedPopup.hide();
    this.activeAutoSpin.setVisible(false);
    this.betPanel.setControlsVisible(false);
  }

  private updateBet(patch: Partial<typeof this.bet>): void {
    this.bet = { ...this.bet, ...patch };
  }

  private onBadgeAnimationEnd = (): void => {
    if (this.badge.fading) this.badge = { ...this.badge, visible: false, fading: false };
  };

  // ── The layout: the prefab geometry per shape, in design units ─────────────────────────

  public static override styles = [
    chromeControlStyles,
    css`
      /* Empty bar area passes pointer events to the canvas (the slam surface); only the controls take them. */
      /* The bands and the landscape multipliers come from the chrome root (\`chromeContentArea\`), the same numbers the grid's content area subtracts. */
      :host { display: block; position: absolute; inset: 0; pointer-events: none; color: #fff; font-weight: 500; transition: opacity 120ms ease-out, visibility 0s linear 0s; --m: var(--slot-landscape-m, 1); --m-right: var(--slot-landscape-m-right, 1); --band-top: calc(var(--slot-band-top, 80) * 1px); --band-left: calc(var(--slot-band-left, 202.6) * 1px); --band-right: calc(var(--slot-band-right, 344.7) * 1px); --band-bottom: calc(var(--slot-band-bottom, 164.5) * 1px); --drop: var(--slot-bar-drop, 0px); --purchase-drop: var(--slot-bar-purchase-drop, 0px); }
      /* Covered by a Pixi overlay: faded out, then taken out of hit-testing; back at once when it closes. */
      :host([covered]) { opacity: 0; visibility: hidden; transition: opacity 120ms ease-in, visibility 0s linear 120ms; }
      button, slot-spin-button, slot-autospin-active, slot-shell-menu, slot-autospin-popup, slot-speed-popup { pointer-events: auto; }
      .group, .desktop-bar, slot-money-label, slot-spin-button, slot-autospin-active, slot-feature-hud, slot-bet-change-popup, slot-shell-menu, slot-autospin-popup, slot-speed-popup { position: absolute; }
      .at { position: absolute; transform: translate(-50%, -50%); }
      [hidden] { display: none !important; }

      /* ── The controls' looks ── */
      .toggle { width: ${TOGGLE_SIZE}px; height: ${TOGGLE_SIZE}px; }
      .toggle .back, .toggle .icon { position: absolute; left: 50%; top: 50%; transform: translate(-50%, -50%); }
      .toggle .back { width: 100%; height: 100%; }
      .toggle:hover .icon, .toggle:hover .back { filter: brightness(0.86); }
      .toggle:active .icon, .toggle:active .back { filter: brightness(0.7); }
      .toggle[disabled] .icon, .toggle[disabled] .back { filter: brightness(0.5); }
      .speed-toggle .icon { width: 44px; height: 65px; margin-top: 0.7px; }
      .speed-toggle .icon.normal { width: 44.58px; height: 65.6px; }
      .speed-toggle .icon.super-a { margin-left: -9.735px; margin-top: -0.7px; }
      .speed-toggle .icon.super-b { margin-left: 9.545px; margin-top: 0.823px; }
      .menu-toggle .back { width: 110.2px; height: 110.2px; }
      .menu-toggle .icon { width: 78.39px; height: 78.39px; }
      .menu-toggle .icon.open { width: 79.71px; height: 79.71px; }
      .buy-bonus { width: ${PURCHASE_SIZE}px; height: ${PURCHASE_SIZE}px; color: #000; font-size: 55.8px; line-height: 60px; white-space: pre; text-align: center; }
      .buy-bonus .face { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; animation: buy-bonus-wiggle 20s linear infinite; }
      .buy-bonus .back { position: absolute; inset: 0; }
      .buy-bonus span { position: relative; top: -4.237px; }
      .buy-bonus .label-box { position: relative; display: flex; align-items: center; justify-content: center; }
      /* 8 degrees either way over 0.6 s, every 20 s (the face wiggles, so the button's anchor transform stays). */
      @keyframes buy-bonus-wiggle { 0%, 3%, 100% { transform: rotate(0deg); } 0.75% { transform: rotate(8deg); } 2.25% { transform: rotate(-8deg); } }
      .buy-bonus:hover { filter: brightness(0.7); }
      .buy-bonus:active { filter: brightness(0.87); }
      .buy-bonus[disabled] { filter: brightness(0.43); }
      .disable { width: ${PURCHASE_SIZE}px; height: ${PURCHASE_SIZE}px; border-radius: 64px; background: #fc8404; color: #000; font-size: 46.6px; }
      .disable:hover { filter: brightness(0.75); }
      .disable[disabled] { filter: brightness(0.37); }
      .gamble { width: 162.9px; height: 65.69px; border-radius: 16px; background: #98d85c; font-size: 37.3px; font-weight: 700; text-shadow: 2px 2px 5px #000; }
      .gamble:hover { filter: brightness(1.06); }
      .hold-text { width: 340.2px; height: 99.69px; display: flex; align-items: center; justify-content: center; text-align: center; font-size: 38.1px; font-weight: 700; line-height: 1.15; text-shadow: 2px 2px 5px #000; }
      .badge { background: #fc8404; display: flex; align-items: center; justify-content: center; font-size: 45px; overflow: hidden; }
      .badge.fading { animation: badge-fade linear forwards; }
      @keyframes badge-fade { to { opacity: 0; } }
      .circle { width: ${BET_BUTTON_SIZE}px; height: ${BET_BUTTON_SIZE}px; }
      .circle:hover { filter: brightness(0.8); }
      .circle:active { filter: brightness(0.45); }
      .circle[disabled] { filter: brightness(0.28); }

      /* ── Desktop: the 2536×212.6 bar scaled to the design width, pinned above the bottom; children at their offsets from its centre. ── */
      .desktop-bar { left: 50%; bottom: 76.96px; width: 2536px; height: 212.6px; transform-origin: bottom center; transform: translateX(-50%) scale(min(1, calc(var(--slot-design-width) / 2536))); }
      .desktop-art { position: absolute; left: 50%; top: 50%; width: 1981px; height: 212.6px; transform: translate(-50%, -50%); }
      .desktop-bar .badge { left: calc(50% - 2.809px); top: calc(50% - 141.1px); width: 1971px; height: 77.15px; }
      .desktop-bar slot-feature-hud { left: calc(50% - 8.265px); top: calc(50% - 182.9px); transform: translate(-50%, -50%); }
      .desktop-bar .buy-bonus, .desktop-bar .disable { left: calc(50% - 1128px); top: 50%; }
      .desktop-bar .menu-toggle { left: calc(50% - 909.5px); top: 50%; }
      .vline { left: calc(50% - 831.2px); top: 50%; width: 5px; height: 154px; background: rgba(255, 255, 255, 0.333); }
      .desktop-bar [data-part="balance-label"] { left: calc(50% - 705.9px); top: 50%; --title-width: 239.8px; --title-height: 63.02px; --title-x: 13.04px; --title-size: 45.6px; --amount-width: 308.9px; --amount-height: 50.4px; --amount-x: 54.38px; }
      .desktop-bar [data-part="win-label"] { left: calc(50% - 217.6px); top: 50%; --title-width: 180.7px; --title-height: 50.4px; --title-x: 0px; --amount-width: 209.5px; --amount-x: 14.43px; }
      .desktop-bar [data-part="bet-label"] { left: calc(50% + 324.1px); top: 50%; --title-width: 215.9px; --title-height: 50.4px; --title-x: 8.267px; --amount-width: 215.9px; --amount-x: 3.816px; }
      .progress { left: calc(50% + 315.515px); top: calc(50% + 60.44px); width: 180px; height: 15px; border-radius: 8px; background: rgba(255, 255, 255, 0.09); }
      .progress .fill { position: absolute; left: 0; top: 0; bottom: 0; border-radius: 8px; background: rgba(255, 255, 255, 0.584); }
      .bet-desktop { left: calc(50% + 515.5px); top: 50%; width: 84.18px; height: 184.1px; }
      .bet-bg { position: absolute; inset: 0; }
      .bet-desktop button { position: absolute; left: 50%; transform: translate(-50%, -50%); }
      .bet-desktop .plus { top: calc(50% - 41.7px); width: 63.3px; height: 63.3px; }
      .bet-desktop .minus { top: calc(50% + 41.7px); width: 70px; height: 70px; }
      .bet-desktop .icon { position: absolute; left: 50%; top: 50%; transform: translate(-50%, -50%); width: 46.65px; height: 46.65px; }
      .bet-desktop .minus .icon { height: 13.87px; }
      .bet-desktop button:hover .icon { filter: brightness(0.8); }
      .bet-desktop button:active .icon { filter: brightness(0.45); }
      .bet-desktop button[disabled] .icon { filter: brightness(0.28); }
      .desktop-bar slot-spin-button, .desktop-bar slot-autospin-active { left: calc(50% + 697.4px); top: 50%; width: 240px; height: 240px; transform: translate(-50%, -50%); }
      .desktop-bar .autospin-toggle { left: calc(50% + 882.1px); top: calc(50% + 58.78px); }
      .desktop-bar .speed-toggle { left: calc(50% + 882.1px); top: calc(50% - 58.78px); }
      .desktop-bar slot-shell-menu { left: calc(50% - 991.71px); top: calc(50% - 103px); width: 0; height: 0; }
      .desktop-bar slot-autospin-popup { left: calc(50% + 237.3px); top: calc(50% - 639.7px); width: 755.4px; height: 529.6px; }
      .desktop-bar slot-speed-popup { left: calc(50% + 278.4px); top: calc(50% - 588.63px); width: 713.2px; height: 478.5px; }
      :host([data-shape="desktop"]) slot-bet-change-popup { display: none; }

      /* ── Mobile: the top strip, the left column and the bottom row scale by the landscape multiplier; the centre cluster holds the shared controls in portrait. ── */
      /* A title's portrait arrangement (decision 78) moves the left and centre groups' controls down from these places: \`--drop\` all of them, \`--purchase-drop\` the Buy Bonus button further; both 0 on the other shapes. */
      .group.top { left: 0; top: 0.4125px; width: calc(100% / var(--m)); height: var(--band-top); transform: scale(var(--m)); transform-origin: top left; }
      .group.top .badge { left: 50%; top: 50%; width: 100%; height: 100%; }
      .group.left { left: 0; top: 0; width: var(--band-left); height: calc(100% / var(--m)); transform: scale(var(--m)); transform-origin: top left; }
      .group.left .buy-bonus, .group.left .disable { left: ${P.purchaseLeft}px; bottom: calc(${P.purchase}px - var(--drop) - var(--purchase-drop)); transform: translate(-50%, 50%); }
      .group.left .menu-toggle { left: 82.52px; bottom: calc(${P.menuToggle}px - var(--drop)); transform: translate(-50%, 50%); }
      .group.bottom { left: 0; bottom: 0; width: calc(100% / var(--m)); height: var(--band-bottom); transform: scale(var(--m)); transform-origin: bottom left; }
      /* The footer shade (decision 44): the numbers' row backed by a gradient from the theme's colour at the screen's bottom edge
         to transparent, \`--slot-footer-shade-height\` bands tall; painted first, so the labels sit over it. The desktop bar has its texture. */
      :host(:not([data-shape="desktop"])) .group.bottom::before { content: ""; position: absolute; left: 0; right: 0; bottom: 0; height: calc(100% * var(--slot-footer-shade-height)); background: linear-gradient(to top, var(--slot-footer-shade), transparent); }
      .group.bottom [data-part="balance-label"] { left: 204.96px; top: calc(50% + 13.56px); --title-width: 400px; --title-x: 13.04px; --title-size: 45.6px; --amount-width: 400px; --amount-x: 13.04px; }
      .group.bottom [data-part="bet-label"] { left: 50%; top: calc(50% + 14.56px); --title-x: 3.816px; --amount-x: 3.816px; }
      .group.bottom [data-part="win-label"] { left: calc(100% - 190px); top: calc(50% + 14.56px); }
      .group.centre { left: 0; bottom: 0; width: 100%; height: 696.5px; }
      .group.centre .hold-text { left: 50%; bottom: calc(584.35px - var(--drop)); transform: translate(-50%, 50%); }
      /* Landscape: left of the spin button at its height, like the bet-change popup — its right edge 20 units inside the column's inner edge, so on 16:9 it sits in the free water beside the frame (narrower landscape overlaps the frame's right rail; accepted). */
      :host([data-shape="mobile_landscape"]) .group.centre .hold-text { left: auto; right: calc(var(--band-right) * var(--m-right) + 20px); bottom: 960px; transform: translate(0, 50%); }
      .group.centre .gamble { left: 50%; bottom: calc(599.45px - var(--drop)); transform: translate(-50%, 50%); }
      .group.centre slot-feature-hud { left: 50%; bottom: calc(348px - var(--drop)); transform: translate(-50%, 50%); }
      .group.centre slot-bet-change-popup { left: 50%; bottom: calc(589.21px - var(--drop)); transform: translate(-50%, 50%); }
      .group.centre .bet-minus { left: calc(50% - ${P.betOffset}px); bottom: calc(360.4px - var(--drop)); transform: translate(-50%, 50%); }
      .group.centre .bet-plus { left: calc(50% + ${P.betOffset}px); bottom: calc(360.4px - var(--drop)); transform: translate(-50%, 50%); }
      .group.centre slot-autospin-active { left: 50%; bottom: calc(357.6px - var(--drop)); width: ${SPIN_SIZE.mobile}px; height: ${SPIN_SIZE.mobile}px; transform: translate(-50%, 50%); }
      .group.centre slot-spin-button { left: 50%; bottom: calc(${P.spin}px - var(--drop)); width: ${SPIN_SIZE.mobile}px; height: ${SPIN_SIZE.mobile}px; transform: translate(-50%, 50%); }
      .group.centre .autospin-toggle { left: calc(100% - 82px); bottom: calc(${P.autospinToggle}px - var(--drop)); transform: translate(-50%, 50%); }
      .group.centre .speed-toggle { left: calc(100% - 82px); bottom: calc(${P.speedToggle}px - var(--drop)); transform: translate(-50%, 50%); }
      /* Landscape: the shared controls move to the right column. */
      .group.right { right: 0; top: 0; width: var(--band-right); height: calc(100% / var(--m-right)); transform: scale(var(--m-right)); transform-origin: top right; }
      .group.right slot-feature-hud { left: calc(50% + 5.736px); top: 50%; transform: translate(-50%, -50%); }
      .group.right slot-bet-change-popup { left: calc(50% - 453.6px); top: 50%; transform: translate(-50%, -50%); }
      .group.right .bet-plus { left: calc(50% - 24.18px); top: calc(50% - 220px); }
      .group.right .bet-minus { left: calc(50% - 24.18px); top: calc(50% + 220px); }
      .group.right slot-autospin-active, .group.right slot-spin-button { left: calc(50% - 24.18px); top: 50%; width: 239px; height: 239px; transform: translate(-50%, -50%); }
      .group.right .autospin-toggle { left: calc(50% + 37.72px); top: calc(50% + 375.1px); }
      .group.right .speed-toggle { left: calc(50% - 86.46px); top: calc(50% + 375.1px); }
      :host(:not([data-shape="desktop"])) slot-shell-menu, :host(:not([data-shape="desktop"])) slot-autospin-popup, :host(:not([data-shape="desktop"])) slot-speed-popup { inset: 0; }
    `,
  ];

  /**
   * The Pixi overlays over the game (a big win, a feature panel, the gamble, the transition): the whole
   * bar family hides while one is open, so no DOM control floats over the art (decision 18).
   */
  public set canvasOverlay(store: CanvasOverlayStore | null) {
    this.unsubscribeCanvasOverlay?.();
    this.unsubscribeCanvasOverlay = null;
    this.canvasOverlayStore = store;
    if (this.isConnected) this.watchCanvasOverlay();
  }

  private watchCanvasOverlay(): void {
    this.unsubscribeCanvasOverlay?.();
    this.unsubscribeCanvasOverlay = this.canvasOverlayStore?.subscribe((s) => this.toggleAttribute("covered", s.open > 0)) ?? null;
  }

  public override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.unsubscribeCanvasOverlay?.();
    this.unsubscribeCanvasOverlay = null;
  }

  public override connectedCallback(): void {
    super.connectedCallback();
    this.watchCanvasOverlay();
    this.menu.viewport = this.viewport;
    this.autoSpinPopup.viewport = this.viewport;
    this.speedPopup.viewport = this.viewport;
  }

  protected override willUpdate(changed: PropertyValues): void {
    super.willUpdate(changed);
    if (!changed.has("view") && !changed.has("layout")) return;
    // The arrangement's drops (decision 78), on the host for the mobile groups' rules; none, no property.
    const drops = portraitBarDrops(this.layout, this.view);
    this.setLength("--slot-bar-drop", drops.controls);
    this.setLength("--slot-bar-purchase-drop", drops.purchase);
  }

  private setLength(name: string, units: number): void {
    if (units > 0) this.style.setProperty(name, `${units}px`);
    else this.style.removeProperty(name);
  }

  protected override updated(): void {
    fitTexts(this.renderRoot);
    // The desktop popovers ignore a press on their own toggle (it closes them through the presenter).
    this.menu.anchor = this.renderRoot.querySelector("[data-part=menu-toggle]");
    this.autoSpinPopup.anchor = this.renderRoot.querySelector("[data-part=autospin-toggle]");
    this.speedPopup.anchor = this.renderRoot.querySelector("[data-part=speed-toggle]");
  }

  /** The children's inputs: the art, the labels, the shape-dependent slot sizes. */
  private applyChildProps(shape: ScreenShape): void {
    const size = SPIN_SIZE[shape];
    this.spinButtonElement.art = this.art;
    this.spinButtonElement.size = size;
    this.activeAutoSpin.art = this.art;
    this.activeAutoSpin.size = size;
    this.activeAutoSpin.label = this.labels.autospinsLeft;
    this.featureHud.variant = shape;
    this.featureHud.labels = this.labels.hud;
    this.betChangePopup.label = this.labels.bet;
    this.menu.art = this.art;
    this.menu.labels = this.labels.menu;
    this.autoSpinPopup.art = this.art;
    this.autoSpinPopup.labels = this.labels.autospin;
    this.speedPopup.art = this.art;
    this.speedPopup.labels = this.labels.speed;
    const layout = shape === "desktop" ? "desktop" : "mobile";
    for (const [label, title] of [[this.balanceLabel, this.labels.balance], [this.betLabel, this.labels.bet], [this.winLabel, this.labels.win]] as const) {
      label.title = title;
      label.layout = layout;
    }
    this.betLabel.accent = this.bet.featureMode;
    this.betLabel.amount = this.bet.text;
    this.betLabel.amountShown = this.bet.valueVisible;
  }

  // ── Templates: the controls, each once, in the shape's group ────────────────────────────

  private barButton(button: BarButton, part: string, className: string, content: unknown, onPress: ((event: Event) => void) | null = pressFeedback, hidden = !button.shown) {
    return html`<button class=${className} type="button" data-part=${part} ?hidden=${hidden} ?disabled=${!button.enabled} @pointerdown=${onPress} @click=${() => button.tap()}>${content}</button>`;
  }

  private autoSpinToggleTemplate() {
    return this.barButton(this.autoSpinToggle, "autospin-toggle", "at toggle bar-button autospin-toggle", html`<div class="back art" style=${artImage(this.art, "autoBetIcon")}></div>`);
  }

  private speedToggleTemplate() {
    const mode = this.speedMode;
    const icon = mode === "turbo"
      ? html`<div class="icon mask" style=${artMask(this.art, "fastIcon", "#ffd220")}></div>`
      : mode === "super_turbo"
        ? html`<div class="icon mask super-a" style=${artMask(this.art, "fastIcon", "#ffd220")}></div><div class="icon mask super-b" style=${artMask(this.art, "fastIcon", "#ff8500")}></div>`
        : html`<div class="icon mask normal" style=${artMask(this.art, "fastIcon", "#ffffff")}></div>`;
    return this.barButton(this.speedButton, "speed-toggle", "at toggle speed-toggle", html`<div class="back art" style=${artImage(this.art, "roundBg")}></div>${icon}`);
  }

  private menuToggleTemplate() {
    const icon = this.menuOpen
      ? html`<div class="icon mask open" style=${artMask(this.art, "close", "#ffffff")}></div>`
      : html`<div class="icon mask" style=${artMask(this.art, "settingsIcon", "#ffffff")}></div>`;
    return this.barButton(this.menuButton, "menu-toggle", "at toggle menu-toggle", html`<div class="back art" style=${artImage(this.art, "roundBg")}></div>${icon}`);
  }

  private purchaseToggleTemplate() {
    const label = html`<span data-fit style="white-space: pre">${this.labels.buyBonus}</span>`;
    // A title's button art that leaves the label less room boxes it (decision 52); otherwise it fits the whole button.
    const box = this.art.buyBonusBtn?.labelBox;
    const fitted = box ? html`<div class="label-box" style=${`width: ${box.width * 100}%; height: ${box.height * 100}%`}>${label}</div>` : label;
    return this.barButton(this.purchaseToggle, "purchase-toggle", "at buy-bonus", html`<div class="face"><div class="back mask" style=${artMask(this.art, "buyBonusBtn", "#bad442")}></div>${fitted}</div>`);
  }

  private disableTemplate() {
    return html`<button class="at disable" type="button" data-part="feature-mode-disable" ?hidden=${!this.disableButton.shown} ?disabled=${!this.disableButton.enabled} @pointerdown=${pressFeedback} @click=${() => this.disableButton.enabled && this.disableBindings?.onDisable()}><span data-fit>${this.labels.disable}</span></button>`;
  }

  private gambleTemplate() {
    return this.barButton(this.gambleEntry, "gamble-entry", "at gamble outline", html`<span data-fit>${this.labels.gamble}</span>`, null);
  }

  private circleBet(button: BarButton, part: "bet-plus" | "bet-minus", frame: "plusCircle" | "minusCircle") {
    return html`<button class="at circle art ${part}" type="button" data-part=${part} ?hidden=${!button.shown || !this.bet.controlsVisible} ?disabled=${!button.enabled} style=${artImage(this.art, frame)} @pointerdown=${pressHaptic} @click=${() => button.tap()}></button>`;
  }

  private badgeTemplate() {
    if (!this.badge.visible) return nothing;
    const fade = this.badge.fading ? `animation-duration: ${this.badge.fadeSeconds}s` : "";
    return html`<div class="at badge ${this.badge.fading ? "fading" : ""}" style=${fade} data-part="feature-mode-badge" data-generation=${this.badge.generation} @animationend=${this.onBadgeAnimationEnd}><span data-fit>${this.badge.title}</span></div>`;
  }

  private desktopTemplate() {
    const betControlsHidden = !this.bet.controlsVisible;
    return html`
      <div class="desktop-bar">
        <div class="desktop-art art" style=${artImage(this.art, "desktopBar")}></div>
        ${this.badgeTemplate()}
        ${this.featureHud}
        ${this.purchaseToggleTemplate()}
        ${this.disableTemplate()}
        ${this.menuToggleTemplate()}
        <div class="at vline"></div>
        ${this.balanceLabel}
        ${this.winLabel}
        <div class="at progress" ?hidden=${betControlsHidden}><div class="fill" style="width: ${this.bet.progress * 100}%"></div></div>
        ${this.betLabel}
        <div class="at bet-desktop" ?hidden=${betControlsHidden}>
          <div class="bet-bg art" style=${artImage(this.art, "betControlsBg")}></div>
          <button class="plus" type="button" data-part="bet-plus" ?disabled=${!this.betPlus.enabled} @pointerdown=${pressHaptic} @click=${() => this.betPlus.tap()}><div class="icon art" style=${artImage(this.art, "betPlusIcon")}></div></button>
          <button class="minus" type="button" data-part="bet-minus" ?disabled=${!this.betMinus.enabled} @pointerdown=${pressHaptic} @click=${() => this.betMinus.tap()}><div class="icon art" style=${artImage(this.art, "betMinusIcon")}></div></button>
        </div>
        ${this.spinButtonElement}
        ${this.activeAutoSpin}
        ${this.autoSpinToggleTemplate()}
        ${this.speedToggleTemplate()}
        ${this.menu}
        ${this.autoSpinPopup}
        ${this.speedPopup}
      </div>
      ${this.betChangePopup}
    `;
  }

  private mobileTemplate() {
    const landscape = this.isLandscape;
    return html`
      <div class="group top">${this.badgeTemplate()}</div>
      <div class="group left">
        ${this.purchaseToggleTemplate()}
        ${this.disableTemplate()}
        ${this.menuToggleTemplate()}
      </div>
      <div class="group bottom">
        ${this.balanceLabel}
        ${this.betLabel}
        ${this.winLabel}
      </div>
      <div class="group centre">
        ${this.holdToSpin && !this.replayMode && this.holdHint ? html`<div class="at hold-text outline" data-part="hold-text">${this.labels.holdToSpin}</div>` : nothing}
        ${this.gambleTemplate()}
        ${landscape ? nothing : html`
          ${this.featureHud}
          ${this.betChangePopup}
          ${this.circleBet(this.betMinus, "bet-minus", "minusCircle")}
          ${this.circleBet(this.betPlus, "bet-plus", "plusCircle")}
          ${this.activeAutoSpin}
          ${this.spinButtonElement}
          ${this.autoSpinToggleTemplate()}
          ${this.speedToggleTemplate()}
        `}
      </div>
      ${landscape ? html`
        <div class="group right">
          ${this.featureHud}
          ${this.betChangePopup}
          ${this.circleBet(this.betPlus, "bet-plus", "plusCircle")}
          ${this.circleBet(this.betMinus, "bet-minus", "minusCircle")}
          ${this.activeAutoSpin}
          ${this.spinButtonElement}
          ${this.autoSpinToggleTemplate()}
          ${this.speedToggleTemplate()}
        </div>` : nothing}
      ${this.menu}
      ${this.autoSpinPopup}
      ${this.speedPopup}
    `;
  }

  protected override render() {
    this.applyChildProps(this.view.shape);
    return this.isDesktop ? this.desktopTemplate() : this.mobileTemplate();
  }
}

export function defineControlBarElement(): void {
  defineSpinButtonElement();
  defineActiveAutoSpinElement();
  defineFeatureHudElement();
  defineBetChangePopupElement();
  defineShellMenuElement();
  defineAutoSpinPopupElement();
  defineSpeedModePopupElement();
  defineMoneyLabelElement();
  defineChromeElement(CONTROL_BAR_TAG, ControlBarElement);
}
